import { useRef } from "react";
import { Link } from "react-router-dom";
import type { ContentItem } from "@/types/content";
import { formatCount, formatDuration, typeLabels } from "@/lib/content";

const RANK_STYLE = ["bg-primary-500", "bg-accent-500", "bg-secondary-500"];

export default function HotScroll({ items }: { items: ContentItem[] }) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const scrollBy = (dir: number) => {
    scrollerRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  return (
    <section className="w-full py-12 md:py-16 bg-background-100">
      <div className="px-4 md:px-8 flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
        <div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 flex items-center gap-2">
            <i className="ri-fire-fill text-primary-500" />
            热门内容榜
          </h2>
          <p className="text-sm text-foreground-600 mt-2">
            根据播放与浏览热度实时排行，大家都在看这些。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="向左滚动"
            onClick={() => scrollBy(-1)}
            className="w-9 h-9 rounded-full border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 flex items-center justify-center transition-colors"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            aria-label="向右滚动"
            onClick={() => scrollBy(1)}
            className="w-9 h-9 rounded-full border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 flex items-center justify-center transition-colors"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-5 overflow-x-auto no-scrollbar px-4 md:px-8 pb-2"
      >
        {items.map((item, i) => (
          <Link
            key={item.id}
            to={`/detail/${item.id}`}
            className="group flex-shrink-0 w-72 rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
          >
            <div className="relative w-full h-40 overflow-hidden bg-background-100">
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - ${typeLabels[item.type]}`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <span
                className={`absolute top-3 left-3 w-7 h-7 rounded-md text-background-50 text-sm font-bold flex items-center justify-center ${
                  RANK_STYLE[i] ?? "bg-foreground-700"
                }`}
              >
                {i + 1}
              </span>
              {item.type === "video" && item.duration > 0 && (
                <span className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-foreground-950/70 text-background-50 text-xs">
                  {formatDuration(item.duration)}
                </span>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-heading font-semibold text-sm text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                {item.title}
              </h3>
              <div className="flex items-center justify-between text-xs text-foreground-500 mt-3">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" /> {formatCount(item.views)}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">
                  {typeLabels[item.type]}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}